import { useState, useEffect } from 'react';
import Stack from 'react-bootstrap/Stack';
import Button from 'react-bootstrap/Button';
import Checkbox from 'components/Forms/Checkbox'; 
import { CHECKBOX_STATES, FORM_FIELDS, FIELD_TSIZE } from 'utilities/constants';
import { hierarchyDefToCheckboxStateReducer, addDuplicateCheckboxReferences, setChildCheckboxValueAndUpdateParent, setParentAndAllChildCheckboxValues, guidsOfCheckboxsWithState, setAllCheckboxValues } from 'components/Forms/Checkbox'
import './ResearchAreaList.scss'

function transformPreferences(preferences, defaultSelections) {
    let duplicateChildren = {};
    let preferenceHierarchy = preferences.reduce((accumulator, currentParent) => {
        return hierarchyDefToCheckboxStateReducer(accumulator, currentParent, duplicateChildren, defaultSelections);
    }, {});

    // backfill duplicate references
    addDuplicateCheckboxReferences(preferenceHierarchy, duplicateChildren);

    return preferenceHierarchy;
}

export default function ResearchAreaList({ accountCreateSettings, isPediatric, setValue, getValues, isAgeLocked }) {

    // For useState initializations
    const [defaultSelections] = getValues(['preferenceGuids']);

    const [preferenceHierarchies, setPreferenceHierarchies] = useState(
        {
            [FORM_FIELDS.AdultPreferences]: transformPreferences(accountCreateSettings.adultPreferences, defaultSelections ),
            [FORM_FIELDS.PediatricPreferences]: transformPreferences(accountCreateSettings.pediatricPreferences, defaultSelections )
        });
    const [allPreferencesSelected, setAllPreferencesSelected] = useState(!isAgeLocked ? null : false);
    
    useEffect(() => {
        
        if (isAgeLocked)
            return;

        const newPreferenceHierarchies = {};

        newPreferenceHierarchies[FORM_FIELDS.AdultPreferences] = transformPreferences(accountCreateSettings.adultPreferences, null);
        newPreferenceHierarchies[FORM_FIELDS.PediatricPreferences] = transformPreferences(accountCreateSettings.pediatricPreferences, null);
        setPreferenceHierarchies(newPreferenceHierarchies);

        // Reset on isPediatric change
        setValue('preferenceGuids', [], { shouldDirty: true });
        setAllPreferencesSelected(false);

        }, // Clear previous state on pediatric change
        [accountCreateSettings.adultPreferences, accountCreateSettings.pediatricPreferences, isPediatric, setValue, isAgeLocked]);

    const handleCheckChange = (e, field, guid, parentGuid, newValue) => {

        let newPreferenceHierarchies = {};
        let selectedPreferenceGuids = [];
        let unSelectedPreferenceGuids = [];

        // Address Parent/Child impacts of change
        if (parentGuid === null) { // ParentCheck
            newPreferenceHierarchies = setParentAndAllChildCheckboxValues(preferenceHierarchies, field, guid, newValue);            
        } else {
            newPreferenceHierarchies = setChildCheckboxValueAndUpdateParent(preferenceHierarchies, field, guid, parentGuid, newValue);
        }
        
        if (isPediatric) {
            // Summarize selections
            [selectedPreferenceGuids,] = guidsOfCheckboxsWithState(newPreferenceHierarchies[FORM_FIELDS.PediatricPreferences], [CHECKBOX_STATES.Checked]);
            // Use to detemine 'select all' state
            [unSelectedPreferenceGuids,] = guidsOfCheckboxsWithState(newPreferenceHierarchies[FORM_FIELDS.PediatricPreferences], [CHECKBOX_STATES.Unchecked]);
        } else {
            // Summarize selections
            [selectedPreferenceGuids,] = guidsOfCheckboxsWithState(newPreferenceHierarchies[FORM_FIELDS.AdultPreferences], [CHECKBOX_STATES.Checked]);
            // Use to detemine 'select all' state
            [unSelectedPreferenceGuids,] = guidsOfCheckboxsWithState(newPreferenceHierarchies[FORM_FIELDS.AdultPreferences], [CHECKBOX_STATES.Unchecked]);
        }

        // Update local state
        setPreferenceHierarchies(newPreferenceHierarchies);
        if (allPreferencesSelected === true &&
            selectedPreferenceGuids.length !== unSelectedPreferenceGuids.length) {
            setAllPreferencesSelected(false);
        } else if (allPreferencesSelected === false &&
            selectedPreferenceGuids.length !== 0 && unSelectedPreferenceGuids.length === 0) {
            setAllPreferencesSelected(true);
        }

        // Update form state
        setValue('preferenceGuids', selectedPreferenceGuids, { shouldDirty: true });
    };

    const handleChangeAll = (checked) => {
        let selectedPreferenceGuids = [];
        let newPreferenceHierarchies = {};
        let field = FORM_FIELDS.AdultPreferences;
        let checkBoxState = CHECKBOX_STATES.Unchecked

        if (isPediatric)
            field = FORM_FIELDS.PediatricPreferences;

        if (checked)
            checkBoxState = CHECKBOX_STATES.Checked

        newPreferenceHierarchies = setAllCheckboxValues(preferenceHierarchies, field, checkBoxState)

        if (checkBoxState === CHECKBOX_STATES.Checked)
            [selectedPreferenceGuids,] = guidsOfCheckboxsWithState(newPreferenceHierarchies[field], [checkBoxState]);

        // Update local state
        setPreferenceHierarchies(newPreferenceHierarchies);
        setAllPreferencesSelected(checked);
        // Update form state
        setValue('preferenceGuids', selectedPreferenceGuids, { shouldDirty: true });
        
    }

    if (preferenceHierarchies === null)
        return (<></>);
    
    const field = isPediatric ? FORM_FIELDS.PediatricPreferences : FORM_FIELDS.AdultPreferences;
    const areas = preferenceHierarchies[field];

    const researchAreaColumns = [[], []];
    let columnIndex = 0;
    Object.keys(areas).forEach((guid, index) => {        
        researchAreaColumns[columnIndex].push(<ResearchAreaCheckbox key={guid} field={field} guid={guid} area={areas[guid]} handleCheckChange={handleCheckChange} />);

        if (((index + 1) > Object.keys(areas).length / 2) && columnIndex === 0)
            columnIndex++;
    });


    return (
        <div>
            <Button variant="link" type="button" className="select-button" style={{ display: (allPreferencesSelected === false) ? 'inherit' : 'none' }} onClick={() => { handleChangeAll(true) }}>Select All</Button>
            <Button variant="link" type="button" className="select-button" style={{ display: (allPreferencesSelected === true) ? 'inherit' : 'none' }} onClick={() => { handleChangeAll(false) }}>Deselect All</Button>

            <div className="researchAreaContainer">
                <Stack>
                    {researchAreaColumns[0].map((researchArea) => (researchArea) )}
                </Stack>
                <Stack>
                    {researchAreaColumns[1].map((researchArea) => (researchArea))}
                </Stack>
            </div>
        </div>
    );
}

function ResearchAreaCheckbox({ field, guid, area, collapse = true, disabled = false, handleCheckChange }) {

    let children = null;
    if (area.children && Object.keys(area.children).length > 0) {
        children = Object.keys(area.children).map((guid) => ({
            label: area.children[guid].name,
            value: area.children[guid].value,
            guid: guid
        }));
    }

    return (
        // key will force a re-render if collapse prop changes to override the internal collapse state
        // allows this parent to make changes without fully lifting the state for all checkboxes 
        <Checkbox
            inputOptions={{ label: area.name, value: area.value, guid: guid, field: field, collapse: collapse, disabled: disabled, controlSize: FIELD_TSIZE.Medium }}
            childInputs={children}
            key={`${guid}${collapse}`}
            onChange={handleCheckChange}
        />
    );
}